// Public API of the fiction services: the sandbox's store (signals), actions and queries.
// Contract: docs/design/sandbox-contract.md §11. Implementation: src/services/fiction/index.ts.
//
//   app/fiction.ts ─ createFictionServices({ data, saves, now, navigate, initial }) ─► FictionHandle
//                                   │
//   FictionStore (signals) ◄────────┤  phase · turn · date · snapshot · queued · pending · deal · notices
//        ▲                          │
//   actions ────────────────────────┴─► Sim (sim/api.ts): legalMoves · evaluate · submit · save · compare
//        └─ autosave after every turn ─► FictionSaveStore (data/saves.ts, IndexedDB 'worldciv-fiction')
//
// The fiction UI (src/ui/fiction) reads `state`, calls `actions` and `queries`, and imports sim/,
// world/ and view/ for types only. The fiction composition root wires `state.snapshot`,
// `state.reality`, `state.mark`, `state.selection` and `state.camera` to the map view.
//
// This store shares nothing with the real game's store (services/state.ts): the fiction route
// never creates the real Services, so no fictional value can reach a real signal or cache.

import type { ReadonlySignal } from '@preact/signals';
import type { DateStr, SourceId } from '../../core/schema.ts';
import type {
  Acceptance, Briefing, Deal, DealTerm, DecisionAnswer, FictionMark, FictionSaveStore, ForkNote, GameEnd, GameSetup,
  LegacyScore, MoveOption, NationSimState, PendingDecision, SaveSummary, SimCause, SimEvent, SimMove, Stance,
} from '../../core/fiction.ts';
import type { FictionSnapshot, FictionWorld, RealityView, WorldInput } from '../../sim/api.ts';
import type { ViewMode } from '../../view/api.ts';
import type { CountryReport, HexDetail, LensResult, VictoryEntry, VictoryTrack } from '../../world/api.ts';
import type { HoverInfo } from '../api.ts';

/**
 * The finished game replayed with no moves from the player, as a yardstick for the legacy score.
 * Run on request (compareIdle), never on its own: it plays a second session turn by turn.
 */
export type IdleRun =
  | { status: 'none' }
  | { status: 'running'; turn: number; of: number }
  | { status: 'done'; legacy: LegacyScore }
  | { status: 'failed'; message: string };

/** URL of the sandbox: '/?timeline=fiction[&fork=YYYY-MM-DD][&nation=ISO3][&load=<save id>]'. */
export const FICTION_ROUTE = {
  param: 'timeline',
  value: 'fiction',
  fork: 'fork',
  nation: 'nation',
  load: 'load',
} as const;

/**
 *   setup ─begin─► forking ─► briefing ─play─► playing ⇄ resolving (endTurn) ─► ended
 *     any step that throws ─► failed (state.error says why; 'Back to setup' returns to setup)
 */
export type FictionPhase = 'setup' | 'forking' | 'briefing' | 'playing' | 'resolving' | 'ended' | 'failed';

export type FictionPanel = 'none' | 'country' | 'deal' | 'diplomacy' | 'victory' | 'log' | 'saves' | 'cause' | 'notes';

/** A notification card: a SimEvent with its date already written as 'Turn 14 (simulated May 2023)'. */
export interface FictionNotice extends SimEvent {
  dateLabel: string;
}

/** One state as the country panel shows it in the sandbox. */
export interface FictionCountry {
  /** World(t).country(iso3, date, player view): `leaders` and `statements` are always empty. */
  report: CountryReport;
  /** Its stance toward the player; null for the player's own state. */
  stance: Stance | null;
  nation: NationSimState | null;
  /** Stats with year ≥ this are simulated and carry the 'Simulated' tag. */
  simulatedFrom: number;
  /** The flag it flew at the fork date (states, not people: no portrait). */
  flag: string | null;
}

export interface FictionState {
  phase: ReadonlySignal<FictionPhase>;
  /** Why the last step failed (phase 'failed'), or a save warning; null otherwise. */
  error: ReadonlySignal<string | null>;
  /** Load warnings of the current game (rules version or static layers differ). */
  warnings: ReadonlySignal<string[]>;
  /** The real data's as-of date: the latest possible fork. */
  realAsOf: ReadonlySignal<DateStr>;
  /** Prefill of the setup screen from the URL ('What if from here?'). */
  draft: ReadonlySignal<Partial<GameSetup>>;
  setup: ReadonlySignal<GameSetup | null>;
  turn: ReadonlySignal<number>;
  /** Simulated date of the turn; null before the fork. */
  date: ReadonlySignal<DateStr | null>;
  /** 'Turn 14 (simulated May 2023)'; every on-screen date of the sandbox uses this form. */
  dateLabel: ReadonlySignal<string | null>;
  /** What the banner prints; null before the fork. */
  mark: ReadonlySignal<FictionMark | null>;
  snapshot: ReadonlySignal<FictionSnapshot | null>;
  lensResult: ReadonlySignal<LensResult | null>;
  /** R held: the map shows `reality.snapshot` (gold) instead of the simulation. */
  realityHeld: ReadonlySignal<boolean>;
  /** Computed when R is first held on a turn, kept until the turn changes. */
  reality: ReadonlySignal<RealityView | null>;
  actionsLeft: ReadonlySignal<number>;
  /** The player's moves this turn, not yet submitted. */
  queued: ReadonlySignal<SimMove[]>;
  pending: ReadonlySignal<PendingDecision[]>;
  answers: ReadonlySignal<DecisionAnswer[]>;
  /** The deal on the deal screen and its live meter. */
  deal: ReadonlySignal<Deal | null>;
  acceptance: ReadonlySignal<Acceptance | null>;
  /** Cards of the last turn, by priority; and everything this game, newest first. */
  notices: ReadonlySignal<FictionNotice[]>;
  log: ReadonlySignal<FictionNotice[]>;
  legacy: ReadonlySignal<LegacyScore | null>;
  /** Change in the legacy total over the last turn. Null on turn 0 and after a load (nothing to compare). */
  legacyChange: ReadonlySignal<number | null>;
  ended: ReadonlySignal<GameEnd | null>;
  idleRun: ReadonlySignal<IdleRun>;
  lens: ReadonlySignal<string | null>;
  mode: ReadonlySignal<ViewMode>;
  selection: ReadonlySignal<{ hex: number; iso3: string | null } | null>;
  hover: ReadonlySignal<HoverInfo | null>;
  panel: ReadonlySignal<FictionPanel>;
  panelArg: ReadonlySignal<string | null>;
  /** Monotonic camera request, forwarded to MapView.flyTo by the composition root. */
  camera: ReadonlySignal<{ seq: number; hex: number; zoom?: number } | null>;
  saves: ReadonlySignal<SaveSummary[]>;
  /** IndexedDB unavailable (private window, blocked storage): 'Saves are off', export still works. */
  savesOff: ReadonlySignal<boolean>;
}

export interface FictionActions {
  /** Fork at setup.forkDate and start a game: setup ─► forking ─► briefing. */
  begin(setup: GameSetup): Promise<void>;
  /** Leave the briefing: turn 0, the camera flies to the player's capital. */
  play(): void;
  /** Queue a move for this turn; an illegal move is not queued, and its verdict says why. */
  queue(move: SimMove): MoveOption;
  unqueue(index: number): void;
  answer(answer: DecisionAnswer): void;
  /** Open the deal screen with `target` (an empty deal from the player). */
  openDeal(target: string): void;
  setDealTerms(terms: DealTerm[]): void;
  /** "What would make this work?": replaces the draft with Sim.suggest when there is one; false when none. */
  suggestDeal(): boolean;
  /** Queue the draft as a 'propose' move and close the deal screen. */
  proposeDeal(): MoveOption;
  closeDeal(): void;
  /** Submit the queued moves and answers, post the turn's cards, autosave. */
  endTurn(): Promise<void>;
  holdReality(on: boolean): void;
  save(name: string): Promise<void>;
  load(id: string): Promise<void>;
  removeSave(id: string): Promise<void>;
  /** The current game as SaveFile JSON text (the UI offers it as a download). */
  exportSave(): string;
  /** Load a SaveFile from JSON text; rejects text that is not a fiction save. */
  importSave(text: string): Promise<void>;
  /** End the game now: ─► ended, with the epilogue. */
  stop(): void;
  /** After the end: replay the same game with no moves from the player, yielding every turn. No-op unless ended. */
  compareIdle(): Promise<void>;
  /** Back to setup for a new game (the current one stays in its autosave). */
  restart(): void;
  /** Leave the sandbox for the real game (navigates to '/'). */
  leave(): void;
  setLens(id: string | null): void;
  setMode(mode: ViewMode): void;
  select(hex: number | null): void;
  hover(info: HoverInfo | null): void;
  openPanel(panel: FictionPanel, arg?: string): void;
  flyTo(hex: number, zoom?: number): void;
  dismiss(noticeId: string): void;
}

export interface FictionQueries {
  /** First and last fork date. */
  forkRange(): { min: DateStr; max: DateStr };
  /** States the player may pick for a fork on `date`, by name. */
  playable(date: DateStr): Array<{ iso3: string; name: string }>;
  briefing(): Briefing | null;
  /** The fork report: what the cut removed or estimated. */
  notes(): ForkNote[];
  country(iso3: string): FictionCountry | null;
  /** `iso3`'s stance toward the player. */
  stance(iso3: string): Stance | null;
  /** The player's moves now, given what is queued; one target or every move. */
  moves(target?: string): MoveOption[];
  /**
   * The player's own-state moves (leave or join a bloc, leave a treaty or war, end a pact, lift a sanction),
   * judged against what is queued. Leaves out what can never apply to this state ("Not a member of X").
   * The sim is asked for every move once a turn; later calls only re-check the survivors.
   */
  ownMoves(): MoveOption[];
  victory(track: VictoryTrack): VictoryEntry[];
  /** A simulated cause by its sim: id, or null. */
  cause(id: string): SimCause | null;
  /** A real pre-fork source (gold), or null; never a sim: id. */
  source(id: SourceId): { url: string; title: string; publisher: string; accessed: DateStr } | null;
  countryAtHex(hex: number): string | null;
  hexInfo(hex: number): HexDetail | null;
  actor(index: number): { id: string; name: string; colors: { primary: string; secondary: string } } | null;
  /** Display name of a state, bloc or treaty id in the current turn's world, e.g. 'eu' → 'European Union'; the id when unknown. */
  name(id: string): string;
  /**
   * Who imposed the real sanction `program` that began on `from`: a state or a bloc ('eu'), as the data records it.
   * A country report expands a bloc into its members, so only this tells them apart. Null when the data has no such record.
   */
  sanctionOwner(program: string, from: DateStr): string | null;
  lensIds(): Array<{ id: string; name: string }>;
  /** Any simulated date as 'Turn N (simulated Mon YYYY)'; a date ≤ the fork as a real date. */
  formatDate(date: DateStr): string;
}

export interface FictionServices {
  state: FictionState;
  actions: FictionActions;
  queries: FictionQueries;
}

export interface FictionOptions {
  /** The real data loaded by app/fiction.ts (data layer). Read by the fork, never mutated. */
  data: WorldInput;
  saves: FictionSaveStore;
  /** Wall clock in ms: new seeds and SaveSlot.savedAt (sim/ never reads a clock). */
  now(): number;
  /** Location change for leave(); tests pass a spy. */
  navigate(url: string): void;
  /** From the URL: prefill the setup, or load a save at start. */
  initial?: { forkDate?: DateStr; player?: string; load?: string };
}

/** What createFictionServices returns to the composition root. */
export interface FictionHandle {
  services: FictionServices;
  /** The current turn's World, for actor labels on the map; null before the fork. */
  world(): FictionWorld | null;
}

/** The factory src/services/fiction/index.ts exports as `createFictionServices`. */
export type CreateFictionServices = (opts: FictionOptions) => FictionHandle;
